import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

export function ProductAnalytics({ refreshKey }) {
  const [days, setDays] = useState("all");
  const [an, setAn] = useState(null);
  const [top, setTop] = useState(null);
  const [byBand, setByBand] = useState(null);
  const [topBy, setTopBy] = useState("revenue");

  const params = () => {
    if (days === "all") return { date_from: "2020-01-01", date_to: "2030-12-31" };
    return { days: Number(days) };
  };

  const load = async () => {
    try {
      const [{ data: a }, { data: t }, { data: bb }] = await Promise.all([
        api.get("/product-sales/analytics", { params: params() }),
        api.get("/product-sales/top-products", { params: { ...params(), by: topBy, limit: 10 } }),
        api.get("/product-sales/by-band", { params: params() }),
      ]);
      setAn(a); setTop(t); setByBand(bb);
    } catch (e) { console.error("[ProductAnalytics]", e); }
  };
  useEffect(() => { load(); }, [days, topBy, refreshKey]);

  if (!an || an.total_quantity === 0) return null;

  return (
    <div className="mb-6" data-testid="product-analytics">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow flex items-center gap-2">
          Vendite per prodotto
          <span className="text-[10px] px-2 py-0.5 rounded bg-neutral-100 text-neutral-600">Fonte: CSV vendite prodotto</span>
          {an.partial_days?.length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800">{an.partial_days.length} giornata parziale</span>
          )}
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32" data-testid="pa-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 giorni</SelectItem>
            <SelectItem value="30">30 giorni</SelectItem>
            <SelectItem value="all">Tutto</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatCard label="Fatturato dettagliato" value={fmtEUR(an.total_revenue)} data-testid="pa-revenue" />
        <StatCard label="Prodotti venduti" value={fmtInt(an.total_quantity)} data-testid="pa-quantity" />
        <StatCard label="Prodotti distinti" value={an.distinct_products} data-testid="pa-distinct" />
        <StatCard label="Prezzo medio effettivo" value={an.avg_effective_price ? fmtEUR(an.avg_effective_price) : "—"} />
        <StatCard label="Giorni con dati" value={an.days_with_data} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="pa-top">
          <div className="flex justify-between items-center mb-2">
            <div className="label-eyebrow">Top 10 prodotti</div>
            <div className="flex gap-1">
              <Button size="sm" variant={topBy==="revenue"?"default":"outline"} onClick={()=>setTopBy("revenue")} data-testid="pa-top-revenue">Fatturato</Button>
              <Button size="sm" variant={topBy==="quantity"?"default":"outline"} onClick={()=>setTopBy("quantity")} data-testid="pa-top-quantity">Quantità</Button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={top?.items||[]} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="item_name_csv" type="category" width={110} tick={{fontSize:10}} />
              <Tooltip formatter={(v)=>topBy==="revenue"?fmtEUR(v):fmtInt(v)} />
              <Bar dataKey={topBy} fill="#8B5A2B" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="pa-by-band">
          <div className="label-eyebrow mb-2">Fatturato per fascia CSV</div>
          <table className="w-full text-xs">
            <thead className="text-neutral-500"><tr><th className="text-left">Fascia</th><th className="text-right">Revenue</th><th className="text-right">%</th><th className="text-right">Qty</th><th className="text-right">Giorni</th></tr></thead>
            <tbody>
              {byBand?.bands?.map(b=>(
                <tr key={b.sales_band_csv} className="border-t border-[#E2D9C8]">
                  <td className="py-1">{b.sales_band_csv}</td>
                  <td className="text-right font-medium">{fmtEUR(b.revenue)}</td>
                  <td className="text-right">{b.pct_revenue}%</td>
                  <td className="text-right">{fmtInt(b.quantity)}</td>
                  <td className="text-right">{b.days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {an.by_day?.length > 0 && (
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white mt-4" data-testid="pa-daily-chart">
          <div className="label-eyebrow mb-2">Vendite giornaliere</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={an.by_day}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{fontSize:10}} />
              <YAxis />
              <Tooltip formatter={(v,n)=>n==="revenue"?fmtEUR(v):fmtInt(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#8B5A2B" name="Fatturato" />
            </LineChart>
          </ResponsiveContainer>
          {an.partial_days?.length > 0 && (
            <div className="text-[10px] text-amber-700 mt-1">Giornate parziali: {an.partial_days.join(", ")} — escluse dai confronti "giorno pieno"</div>
          )}
        </div>
      )}
    </div>
  );
}
